import type { ThemeName } from 'tamagui'
import type { GetThemeValueForKey } from '@tamagui/core'

export const THEME_COLOR_FAMILIES = [
  'orange',
  'yellow',
  'green',
  'blue',
  'purple',
  'pink',
  'red',
  'gray',
  'bronze',
  'gold',
] as const

export type ThemeColorFamily = (typeof THEME_COLOR_FAMILIES)[number]

type ThemeColorStep = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
type FamilyColorToken = `$${ThemeColorFamily}${ThemeColorStep}`
type ThemeTextToken = GetThemeValueForKey<'color'>
type ThemeBackgroundToken = GetThemeValueForKey<'backgroundColor'>
type ThemeBorderToken = GetThemeValueForKey<'borderColor'>

export type ThemePalette = {
  family: ThemeColorFamily | null
  pageSurface: ThemeBackgroundToken
  panelSurface: ThemeBackgroundToken
  fieldSurface: ThemeBackgroundToken
  chipSurface: ThemeBackgroundToken
  borderColor: ThemeBorderToken
  titleColor: ThemeTextToken
  bodyColor: ThemeTextToken
  mutedColor: ThemeTextToken
  accentSurface: ThemeBackgroundToken
  accentText: ThemeTextToken
  iconSurface: ThemeBackgroundToken
  iconColor: ThemeTextToken
  dangerSurface: ThemeBackgroundToken
  dangerText: ThemeTextToken
}

const THEME_COLOR_FAMILY_SET = new Set<ThemeColorFamily>(THEME_COLOR_FAMILIES)

export function getThemeColorFamily(theme: ThemeName): ThemeColorFamily | null {
  const [scheme, family] = theme.split('_')

  if ((scheme !== 'light' && scheme !== 'dark') || !family) {
    return null
  }

  return THEME_COLOR_FAMILY_SET.has(family as ThemeColorFamily)
    ? (family as ThemeColorFamily)
    : null
}

export function getThemePalette(theme: ThemeName): ThemePalette {
  const family = getThemeColorFamily(theme)
  const isDarkMode = theme.startsWith('dark')

  if (!family) {
    return {
      family: null,
      pageSurface: isDarkMode ? '$color1' : '$color2',
      panelSurface: isDarkMode ? '$color2' : '$background',
      fieldSurface: isDarkMode ? '$color2' : '$color1',
      chipSurface: isDarkMode ? '$color3' : '$color2',
      borderColor: '$borderColor',
      titleColor: '$color12',
      bodyColor: '$color11',
      mutedColor: '$color10',
      accentSurface: '$color3',
      accentText: '$color12',
      iconSurface: '$color3',
      iconColor: '$color10',
      dangerSurface: '$red3',
      dangerText: '$red10',
    }
  }

  const pageStep = isDarkMode ? 1 : 2
  const panelStep = isDarkMode ? 2 : 3
  const chipStep = isDarkMode ? 3 : 4

  return {
    family,
    pageSurface: `$${family}${pageStep}` as ThemeBackgroundToken,
    panelSurface: `$${family}${panelStep}` as ThemeBackgroundToken,
    fieldSurface: `$${family}${panelStep}` as ThemeBackgroundToken,
    chipSurface: `$${family}${chipStep}` as ThemeBackgroundToken,
    borderColor: `$${family}6` as ThemeBorderToken,
    titleColor: `$${family}12` as ThemeTextToken,
    bodyColor: `$${family}11` as ThemeTextToken,
    mutedColor: `$${family}10` as ThemeTextToken,
    accentSurface: `$${family}${chipStep}` as ThemeBackgroundToken,
    accentText: `$${family}12` as ThemeTextToken,
    iconSurface: `$${family}${chipStep}` as ThemeBackgroundToken,
    iconColor: `$${family}10` as ThemeTextToken,
    dangerSurface: `$${family}${chipStep}` as ThemeBackgroundToken,
    dangerText: `$${family}12` as ThemeTextToken,
  }
}